import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Theater } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { isStudioPartner } from '../lib/partnerType';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';

interface TheatricalListingCardProps {
  workId: string;
}

export default function TheatricalListingCard({ workId }: TheatricalListingCardProps) {
  const { publisher } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listed, setListed] = useState(false);
  const [price, setPrice] = useState('');
  const [releaseDate, setReleaseDate] = useState('');

  const showCard =
    publisher?.id &&
    isStudioPartner(publisher.business_type, publisher.partner_type);

  useEffect(() => {
    if (!showCard || !workId) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error } = await supabase
        .from('works')
        .select('theatrical_listed, theatrical_ticket_price_usd, theatrical_public_release_date')
        .eq('id', workId)
        .eq('publisher_id', publisher!.id)
        .single();
      if (cancelled) return;
      if (error) {
        toast.error('Failed to load theatrical listing');
      } else if (data) {
        setListed(data.theatrical_listed);
        setPrice(data.theatrical_ticket_price_usd?.toString() ?? '');
        setReleaseDate(data.theatrical_public_release_date ?? '');
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [showCard, workId, publisher?.id]);

  const saveListing = async () => {
    if (!publisher?.id) return;
    const parsedPrice = Number(price);
    if (listed && (!Number.isFinite(parsedPrice) || parsedPrice <= 0 || !releaseDate)) {
      toast.error('Set a ticket price and streaming unlock date to list on the marketplace');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('works')
        .update({
          theatrical_listed: listed,
          theatrical_ticket_price_usd: listed ? parsedPrice : null,
          theatrical_public_release_date: listed ? releaseDate : null,
        })
        .eq('id', workId)
        .eq('publisher_id', publisher.id);
      if (error) throw error;
      toast.success(listed ? 'Title listed on Theatre Marketplace' : 'Theatrical listing removed');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to update listing');
    } finally {
      setSaving(false);
    }
  };

  if (!showCard) return null;

  return (
    <Card className="partner-card">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Theater className="h-4 w-4 text-theatre-300" />
          <CardTitle className="text-base">Theatre Marketplace</CardTitle>
          {listed && <Badge variant="secondary">Listed</Badge>}
        </div>
        <CardDescription>
          List this title for cinema booking before streaming goes live. Set your ticket price and
          streaming unlock date — cinema partners book at your price.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading listing…
          </p>
        ) : (
          <>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={listed} onCheckedChange={v => setListed(v === true)} />
              List on Theatre Marketplace
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor={`theatrical-price-${workId}`}>Ticket price (USD)</Label>
                <Input
                  id={`theatrical-price-${workId}`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={price}
                  onChange={e => setPrice(e.target.value)}
                  disabled={!listed}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`theatrical-unlock-${workId}`}>Streaming unlock date</Label>
                <Input
                  id={`theatrical-unlock-${workId}`}
                  type="date"
                  value={releaseDate}
                  onChange={e => setReleaseDate(e.target.value)}
                  disabled={!listed}
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={saveListing} disabled={saving} size="sm">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  'Save listing'
                )}
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/theatre-marketplace">View all listings</Link>
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
